import * as borogove from "borogove";
import Fuse from "fuse.js";
import {
	Chip,
	CircularProgress,
	Fab,
	Menu,
	MenuItem,
	Dialog,
	ListItemIcon,
	ListSubheader,
	useMediaQuery,
} from "@mui/material";
import { QRCanvas, frontalCamera } from "qr/dom.js";
import { Virtuoso } from "react-virtuoso";
import { useQueryState } from "nuqs";
import { useState, useEffect, useRef, useCallback, useContext } from "react";
import {
	Outlet,
	Navigate,
	useNavigate,
	useParams,
	useSearchParams,
	useOutletContext,
	useMatch,
} from "react-router";
import {
	AddUserButton,
	ArrowButton,
	Avatar,
	Button,
	Conversation,
	ConversationHeader,
	MainContainer,
	Search,
	Sidebar,
	TypingIndicator,
	VideoCallButton,
	VoiceCallButton,
} from "@chatscope/chat-ui-kit-react";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faPencil,
	faPlus,
	faXmark,
	faMagnifyingGlassArrowRight,
	faThumbtack,
} from "@fortawesome/free-solid-svg-icons";
import { useDebounce } from "../hooks/use-debounce";

import { AvatarWithPlaceholder } from "./AvatarWithPlaceholder";
import { AwaitOnce } from "./AwaitOnce";
import { confirmDialog } from "./ConfirmDialog";
import { Profile } from "./Profile";
import { SettingsContext } from "./Settings";
import { href, useEffectEvent, useChatExtras, useChats, Chat } from "../util";

borogove.Config.relativeHashUri = true;

export const Chats = () => {
	const params = useParams();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [searchQ, setSearchQ] = useQueryState("q");
	const [searchTag, setSearchTag] = useQueryState("tag");
	const { persistence, client, status, setStatus, logout } = useOutletContext<{
		persistence: borogove.Persistence & borogove.persistence.KeyValueStore;
		client: borogove.Client;
		status: string;
		setStatus: (status: string) => void;
		logout: (completely: boolean) => void;
	}>();
	const { messageRequests } = useContext(SettingsContext);

	const subnav = useMatch("/accounts/:accountId/:subnav/*")?.params?.subnav;
	const showSidebar = !params.chatId && subnav;
	const showRightSidebar = !!useMatch(
		"/accounts/:accountId/:subnav/:chatId/details",
	);

	const chats = useChats(messageRequests, client);
	const activeChat = params.chatId
		? chats.find((c) => c.chatId === params.chatId)
		: null;

	const innerChatRef = useRef<{
		addMention: (member: borogove.Member) => void;
	}>({ addMention: () => {} });

	const [typingMembers, setTypingMembers] = useState<
		Record<string, borogove.Member[]>
	>({});
	useEffect(() => {
		const userStateToken = client.addUserStateListener(
			(memberId, chatId, threadId, state) => {
				void threadId;

				if (state === borogove.UserState.Composing) {
					(async () => {
						const member =
							(await client.getChat(chatId)?.getMemberDetails([memberId])) ??
							[];
						setTypingMembers((existing) => ({
							...existing,
							[chatId]: [
								...(existing[chatId] || []).filter(
									(member) => member.id !== memberId,
								),
								...(member[0] ? [member[0]] : []),
							],
						}));
					})();
				} else {
					setTypingMembers((existing) => ({
						...existing,
						[chatId]: (existing[chatId] || []).filter(
							(member) => member.id !== memberId,
						),
					}));
				}
			},
		);

		return () => {
			client.removeEventListener(userStateToken);
		};
	}, [client]);

	const { getChatExtras, setChatExtras } = useChatExtras(client.accountId());

	// Request permissions once the user is logged in and seeing the app
	useEffect(() => {
		if (navigator.storage && navigator.storage.persist) {
			navigator.storage.persist();
		}

		(async () => {
			if (Notification.requestPermission) {
				await Notification.requestPermission();
			}
		})();
	}, []);

	useEffect(() => {
		const clickHandler = (e: MouseEvent) => {
			const target =
				e.target instanceof HTMLElement ? e.target.closest("a") : null;

			if (target && target.href.startsWith("xmpp:")) {
				e.preventDefault();
				if (screen.width < 576) {
					navigate(
						href(
							"/accounts/:accountId/chats",
							{
								accountId: client.accountId(),
							},
							new URLSearchParams({ q: target.href }),
						),
					);
				} else {
					setSearchQ(target.href);
				}
			}
		};
		document.addEventListener("click", clickHandler);

		return () => {
			document.removeEventListener("click", clickHandler);
		};
	}, [setSearchQ, client, navigate]);

	const debouncedSearch = useDebounce(searchQ, 300);
	const [availableChats, setAvailableChats] = useState({
		q: "",
		chats: [] as borogove.AvailableChat[],
	});
	const [addContactAnchorEl, setAddContactAnchorEl] =
		useState<null | HTMLElement>(null);
	const [addRoleAnchorEl, setAddRoleAnchorEl] = useState<null | HTMLElement>(
		null,
	);

	const [popupMember, setPopupMember] = useState<borogove.Member | undefined>();
	const [scanQRCode, setScanQRCode] = useState(false);
	const [qrStatus, setQRStatus] = useState("Loading camera...");
	const qrDecodeTimeout = useRef<number | null>(null);
	const qrCamera = useRef<Awaited<ReturnType<typeof frontalCamera>> | null>(
		null,
	);
	const qrScanVideo = useCallback(
		(video: HTMLVideoElement) => {
			if (qrDecodeTimeout.current) {
				clearTimeout(qrDecodeTimeout.current);
				qrDecodeTimeout.current = null;
			}
			if (qrCamera.current) {
				qrCamera.current.stop();
				qrCamera.current = null;
			}
			if (scanQRCode && video) {
				const canvas = new QRCanvas({}, { cropToSquare: false });
				frontalCamera(video).then((camera) => {
					qrCamera.current = camera;
					const checkForQR = () => {
						const data = camera.readFrame(canvas);
						if (data) {
							if (data.startsWith("xmpp:")) {
								setSearchQ(data);
								setScanQRCode(false);
							} else {
								setQRStatus("Invalid QR Code found: " + data);
							}
						} else {
							setQRStatus("No QR Code found");
						}
						qrDecodeTimeout.current = setTimeout(() => {
							checkForQR();
						}, 100);
					};
					checkForQR();
				});
			}
		},
		[scanQRCode, setSearchQ],
	);

	const startChat = (availableChat: borogove.AvailableChat, trust: boolean) => {
		const chat = client.startChat(availableChat);
		if (trust) chat.setTrusted(trust);
		chat.bookmark();
		return chat;
	};

	const conversationInfo = (c: borogove.Chat) => {
		let info: string | JSX.Element = c.preview();
		const extras = getChatExtras(c.chatId);
		if (c.chatId !== params.chatId && extras?.draftText) {
			info = "Draft: " + extras.draftText;
		}
		if (c.syncing()) {
			info = `Syncing...`;
		}
		if (c.callStatus() === borogove.calls.CallStatus.Incoming) {
			info = "📲 " + info;
		}
		if (c.callStatus() === borogove.calls.CallStatus.Ongoing) {
			info = "📞 " + info;
		}
		if ((typingMembers[c.chatId] || []).length > 0) {
			info = <TypingIndicator content={info} />;
		}

		return {
			fn: c.getDisplayName(),
			photo: c.getPhoto(),
			placeholder: c.getPlaceholder(),
			info,
		};
	};

	const searchChats = useEffectEvent(async () => {
		const search = searchQ;
		if (search) {
			setAvailableChats({ q: search, chats: [] });
			const iterator = client.findAvailableChats(search);
			for await (const availableChat of iterator) {
				setAvailableChats((existing) =>
					availableChat && existing.q === search
						? { q: search, chats: [...existing.chats, availableChat] }
						: existing,
				);
			}
		}
	});
	useEffect(() => {
		searchChats();
	}, [debouncedSearch]);

	const chatFuse = new Fuse<Chat>(chats, {
		useExtendedSearch: true,
		threshold: 0.4,
		ignoreDiacritics: true,
		keys: [
			{
				name: "fn",
				weight: 2,
				getFn: (c: Chat) => c.getDisplayName(),
			},
			"chatId",
			{
				name: "tags",
				getFn: (c: Chat) => c.getTags(),
			},
		],
	});

	const chatFilter = () => {
		const contains: Record<string, boolean> = {};
		const results = searchQ
			? chatFuse.search(searchQ).map((r) => r.item)
			: chats;
		for (const r of results) {
			contains[r.chatId] = true;
		}
		if (availableChats.q === searchQ) {
			for (const ac of availableChats.chats) {
				if (!contains[ac.chatId]) {
					const found = chats.find((c) => c.chatId === ac.chatId);
					if (found) results.push(found);
				}
			}
		}
		return searchTag
			? results.filter((c) => c.getTags().includes(searchTag))
			: results;
	};

	const [searchResultLimit, setSearchResultLimit] = useState(5);
	const availableChatsForDisplay =
		searchQ && searchQ === availableChats.q
			? availableChats.chats.filter(
					(result) => !chats.find((c) => c.chatId === result.chatId),
				)
			: [];
	const filteredChats = chatFilter();
	const loadMoreButton = searchResultLimit < availableChatsForDisplay.length;

	const [selectContact, setSelectContact] = useState<
		null | ((chat: Chat) => void)
	>(null);
	const [selectContactQ, setSelectContactQ] = useState("");
	const filteredSelectableContacts = (
		selectContactQ ? chatFuse.search(selectContactQ).map((r) => r.item) : chats
	).filter((c) => c instanceof borogove.DirectChat);

	const activeChatIsInvite = activeChat ? activeChat.__isInvite : false;

	const renderConversations: Array<Chat | borogove.AvailableChat> = (
		availableChatsForDisplay.slice(0, searchResultLimit) as Array<
			Chat | borogove.AvailableChat
		>
	).concat(filteredChats.filter((c) => !c.__isInvite));

	const apps = filteredChats.filter((c) => c.isApp());
	const invites = filteredChats.filter((c) => c.__isInvite);

	const selfChat = chats.find((c) => c.chatId === client.accountId());
	const tags = new Set(chats.flatMap((c) => c.getTags()));
	const anyApps = !!chats.find((c) => c.isApp());
	const anyInvites = !!chats.find((c) => c.__isInvite);
	const activeInTab = useRef<{ chats?: string; apps?: string }>({});
	const isMobile = useMediaQuery("(max-width: 576px)");
	useEffect(() => {
		if (
			(!anyInvites && subnav === "requests") ||
			(!anyApps && subnav === "apps")
		) {
			navigate(
				href(
					"/accounts/:accountId/chats",
					{
						accountId: client.accountId(),
					},
					searchParams,
				),
			);
		} else if (!activeChat?.chatId && !isMobile) {
			if (subnav === "chats" && activeInTab.current.chats) {
				navigate(
					href(
						"/accounts/:accountId/chats/:chatId",
						{
							accountId: client.accountId(),
							chatId: activeInTab.current.chats,
						},
						searchParams,
					),
				);
			}

			if (subnav === "apps" && activeInTab.current.apps) {
				navigate(
					href(
						"/accounts/:accountId/apps/:chatId",
						{
							accountId: client.accountId(),
							chatId: activeInTab.current.apps,
						},
						searchParams,
					),
				);
			}
		}

		const tabState = activeInTab.current;
		return () => {
			if (subnav === "chats") tabState.chats = activeChat?.chatId;
			if (subnav === "apps") tabState.apps = activeChat?.chatId;
		};
	}, [
		anyApps,
		anyInvites,
		subnav,
		activeChat?.chatId,
		client,
		navigate,
		searchParams,
		isMobile,
	]);

	return (
		<MainContainer responsive>
			<Sidebar
				position="left"
				scrollable={false}
				className={showSidebar ? "active" : ""}
			>
				<ConversationHeader>
					<ConversationHeader.Content>
						<h1>
							{subnav === "apps"
								? "Apps"
								: subnav === "requests"
									? "Message Requests"
									: "Messages"}
						</h1>
						{selfChat && (selfChat.status.emoji || selfChat.status.text) && (
							<small className="status" title="Status">
								<span className="emoji">{selfChat.status.emoji}</span>
								<span className="text">{selfChat.status.text}</span>
							</small>
						)}
					</ConversationHeader.Content>
					<ConversationHeader.Actions>
						{subnav !== "apps" && subnav !== "requests" && (
							<Button
								title="Start a chat"
								icon={<FontAwesomeIcon icon={faPencil} />}
								onClick={(e) => setAddContactAnchorEl(e.currentTarget)}
							/>
						)}
						<Menu
							open={!!addContactAnchorEl}
							anchorEl={addContactAnchorEl}
							onClose={() => setAddContactAnchorEl(null)}
						>
							<ListSubheader>Start a chat</ListSubheader>
							<MenuItem>
								<form
									onSubmit={(e) => {
										e.preventDefault();
										const q = e.currentTarget.querySelector("input")?.value;
										setSearchQ(q || null);
										setAddContactAnchorEl(null);
									}}
								>
									<Search
										placeholder="Start by ID or URI"
										onKeyDown={(e) => e.stopPropagation()}
									/>
								</form>
							</MenuItem>
							<MenuItem
								onClick={() => {
									setScanQRCode(true);
									setAddContactAnchorEl(null);
								}}
							>
								<ListItemIcon>
									<QrCodeScannerIcon />
								</ListItemIcon>
								Scan QR Code
							</MenuItem>
						</Menu>
					</ConversationHeader.Actions>
				</ConversationHeader>
				<AwaitOnce
					key={client.accountId() + " " + status}
					resolve={() => client.availableChatSources()}
					fallback={
						<Search
							placeholder="Find or start a chat"
							value={searchQ || ""}
							onChange={(q) => setSearchQ(q || null)}
							onClearClick={() => setSearchQ(null)}
						/>
					}
				>
					{(sources) => (
						<Search
							placeholder="Find or start a chat"
							title={"Find or start a chat by " + sources.join(", ")}
							value={searchQ || ""}
							onChange={(q) => setSearchQ(q || null)}
							onClearClick={() => {
								setSearchQ(null);
								setSearchResultLimit(5);
							}}
							onFocus={(e) =>
								((e.target as HTMLInputElement).placeholder =
									sources.join(", "))
							}
							onBlur={(e) =>
								((e.target as HTMLInputElement).placeholder =
									"Find or start a chat")
							}
						/>
					)}
				</AwaitOnce>
				{tags.size > 0 && (
					<select
						id="tag-nav"
						onChange={(e) =>
							setSearchTag(e.target.value ? e.target.value : null)
						}
					>
						<option value="">All Chats</option>
						{tags.keys().map((t) => (
							<option value={t} key={t} selected={searchTag === t}>
								{t}
							</option>
						))}
					</select>
				)}
				{subnav !== "apps" && subnav != "requests" && (
					<nav aria-label="Chat List">
						{searchQ && searchQ.length > 0 && (
							<Conversation
								name="Search messages for"
								info={searchQ}
								onClick={() => {
									navigate(
										href(
											"/accounts/:accountId/search",
											{
												accountId: client.accountId(),
											},
											searchParams,
										),
									);
								}}
							>
								<Conversation.Operations visible>
									<Button
										title="Search messages"
										icon={
											<FontAwesomeIcon icon={faMagnifyingGlassArrowRight} />
										}
										onClick={() => {
											navigate(
												href(
													"/accounts/:accountId/search",
													{
														accountId: client.accountId(),
													},
													searchParams,
												),
											);
										}}
									/>
								</Conversation.Operations>
							</Conversation>
						)}
						<Virtuoso
							tabIndex={undefined}
							onKeyDown={(e) => {
								const active = document.activeElement as HTMLDivElement;
								const scroller = (e.target as HTMLDivElement)?.parentElement
									?.parentElement?.parentElement?.parentElement;
								if (
									e.key === "Tab" &&
									active?.dataset?.virtuosoScroller !== "true" &&
									scroller &&
									active
								) {
									if (
										scroller.getBoundingClientRect().bottom -
											active.getBoundingClientRect().bottom <
										75
									) {
										scroller.scrollBy(0, 75);
									}

									if (
										active.getBoundingClientRect().top -
											scroller.getBoundingClientRect().top <
										75
									) {
										scroller.scrollBy(0, -75);
									}
								}
							}}
							totalCount={renderConversations.length + (loadMoreButton ? 1 : 0)}
							itemContent={(index) => {
								if (loadMoreButton && index === searchResultLimit) {
									return (
										<Button
											onClick={() => setSearchResultLimit((was) => was + 10)}
										>
											Load More Results
										</Button>
									);
								}
								if (loadMoreButton && index > searchResultLimit) index--;
								const result = renderConversations[index];
								if (result instanceof borogove.AvailableChat) {
									return (
										<Conversation
											key={result.chatId}
											name={result.displayName}
											info={result.note}
											onClick={() =>
												navigate(
													href(
														"./chats/:chatId",
														{
															chatId: startChat(result, false)?.chatId,
														},
														searchParams,
													),
												)
											}
										>
											<Conversation.Operations visible>
												{result.canAudioCall() && (
													<VoiceCallButton
														title="Start audio call"
														onClick={() =>
															startChat(result, true).startCall(true, false)
														}
													/>
												)}
												{result.canVideoCall() && (
													<VideoCallButton
														title="Start video call"
														onClick={() =>
															startChat(result, true).startCall(true, true)
														}
													/>
												)}
												{!result.isChannel() && (
													<AddUserButton
														title="Add contact"
														onClick={() =>
															navigate(
																href(
																	"./chats/:chatId",
																	{
																		chatId: startChat(result, true)?.chatId,
																	},
																	searchParams,
																),
															)
														}
													/>
												)}
												{result.isChannel() && (
													<ArrowButton
														title="Join Channel"
														direction="right"
														onClick={() =>
															navigate(
																href(
																	"./chats/:chatId",
																	{
																		chatId: startChat(result, true)?.chatId,
																	},
																	searchParams,
																),
															)
														}
													/>
												)}
											</Conversation.Operations>
										</Conversation>
									);
								}

								const c: borogove.Chat = result;

								// Helper for getting the data of the first participant
								const info = conversationInfo(c);

								return (
									<Conversation
										key={c.chatId}
										name={info.fn}
										info={info.info}
										active={params.chatId === c.chatId}
										unreadCnt={c.unreadCount()}
										accessKey={index < 10 ? "" + (index + 1) : undefined}
										style={
											invites.length > 0 &&
											index >= renderConversations.length - 1
												? { paddingBottom: "5em" }
												: {}
										}
										aria-label={info.fn}
										tabIndex={0}
										onKeyDown={(e) =>
											(e.key === "Enter" || e.key === " ") &&
											e.currentTarget.click()
										}
										onClick={async () => {
											const scrollTo = getChatExtras(c.chatId)?.scrollTo;
											const readUpTo = await c.readUpTo();
											const readUpToId = readUpTo?.serverId;
											navigate(
												href(
													"./chats/:chatId",
													{ chatId: c.chatId },
													searchParams,
												) +
													(scrollTo || readUpToId
														? `#message:${scrollTo || readUpToId}`
														: ""),
											);
										}}
									>
										<AvatarWithPlaceholder
											as={Avatar}
											photoUri={info.photo}
											placeholderUri={info.placeholder}
											displayName={info.fn}
											aria-hidden="true"
										/>
										<Conversation.Operations
											visible={c.uiState === borogove.UiState.Pinned}
										>
											<Button
												style={{
													opacity:
														c.uiState === borogove.UiState.Pinned ? 0.9 : 0.5,
												}}
												icon={<FontAwesomeIcon icon={faThumbtack} />}
												title={
													c.uiState === borogove.UiState.Pinned
														? "Unpin Chat"
														: "Pin Chat"
												}
												onClick={() => c.togglePinned()}
											/>
										</Conversation.Operations>
									</Conversation>
								);
							}}
						/>
						{invites.length > 0 && (
							<Fab
								color="secondary"
								variant="extended"
								onClick={() =>
									navigate(
										href(
											"/accounts/:accountId/requests",
											{
												accountId: client.accountId(),
											},
											searchParams,
										),
									)
								}
							>
								<Chip label={invites.length} /> Message requests{" "}
								<ArrowForward />
							</Fab>
						)}
					</nav>
				)}
				{subnav === "apps" && (
					<Virtuoso
						aria-label="App List"
						tabIndex={-1}
						totalCount={apps.length}
						itemContent={(index) => {
							const c: borogove.Chat = apps[index];

							// Helper for getting the data of the first participant
							const info = conversationInfo(c);

							return (
								<Conversation
									key={c.chatId}
									name={info.fn}
									active={params.chatId === c.chatId}
									aria-label={info.fn}
									tabIndex={0}
									onKeyDown={(e) =>
										(e.key === "Enter" || e.key === " ") &&
										e.currentTarget.click()
									}
									onClick={() => {
										navigate(
											href(
												"./apps/:chatId",
												{ chatId: c.chatId },
												searchParams,
											),
										);
									}}
								>
									<AvatarWithPlaceholder
										as={Avatar}
										photoUri={info.photo}
										placeholderUri={info.placeholder}
										displayName={info.fn}
									/>
								</Conversation>
							);
						}}
					/>
				)}
				{subnav === "requests" && (
					<>
						<Virtuoso
							aria-label="Message Request List"
							tabIndex={-1}
							totalCount={invites.length}
							itemContent={(index) => {
								const c: borogove.Chat = invites[index];

								// Helper for getting the data of the first participant
								const info = conversationInfo(c);

								return (
									<Conversation
										key={c.chatId}
										name={info.fn}
										info={info.info}
										active={params.chatId === c.chatId}
										style={
											index >= invites.length - 1
												? { paddingBottom: "5em" }
												: {}
										}
										aria-label={info.fn}
										tabIndex={0}
										onKeyDown={(e) =>
											(e.key === "Enter" || e.key === " ") &&
											e.currentTarget.click()
										}
										onClick={() => {
											navigate(
												href(
													"./requests/:chatId",
													{ chatId: c.chatId },
													searchParams,
												),
											);
										}}
									>
										<AvatarWithPlaceholder
											as={Avatar}
											photoUri={info.photo}
											placeholderUri={info.placeholder}
											displayName={info.fn}
										/>
									</Conversation>
								);
							}}
						/>
						<Fab
							color="error"
							variant="extended"
							onClick={() => {
								confirmDialog({
									message:
										"Do you want to report every message request as SPAM?",
									accept: () => {
										for (const invite of invites) {
											invite.block(true, invite.lastMessage);
										}
									},
									reject: () => {},
								});
							}}
						>
							Report all as SPAM
						</Fab>
					</>
				)}
			</Sidebar>

			{params.chatId && !activeChat ? (
				<Navigate
					to={href("/accounts/:accountId", { accountId: client.accountId() })}
				/>
			) : (
				<Outlet
					context={{
						active: !showSidebar && !showRightSidebar,
						isInvite: activeChatIsInvite,
						activeChat,
						persistence,
						getChatExtras,
						setChatExtras,
						selectContact: (f: (contactChat: borogove.Chat) => void) =>
							setSelectContact(() => f),
						setPopupMember,
						innerChatRef,
						client,
						status,
						setStatus,
						logout,
						typingMembers: params.chatId
							? typingMembers[params.chatId] || []
							: [],
					}}
				/>
			)}

			{selectContact && (
				<Dialog
					open={!!selectContact}
					onClose={() => {
						setSelectContact(null);
					}}
					fullScreen={isMobile}
					id="select-contact-dialog"
				>
					<Search
						placeholder="Find Contact"
						value={selectContactQ}
						onChange={(q) => setSelectContactQ(q)}
						onClearClick={() => setSelectContactQ("")}
					/>
					<Virtuoso
						aria-label="Select Contact"
						totalCount={filteredSelectableContacts.length}
						itemContent={(index) => {
							const c: borogove.Chat = filteredSelectableContacts[index];

							// Helper for getting the data of the first participant
							const info = conversationInfo(c);

							return (
								<Conversation
									key={c.chatId}
									name={info.fn}
									aria-label={info.fn}
									tabIndex={0}
									onKeyDown={(e) =>
										(e.key === "Enter" || e.key === " ") &&
										e.currentTarget.click()
									}
									onClick={() => {
										selectContact(c);
										setSelectContact(null);
									}}
								>
									<AvatarWithPlaceholder
										as={Avatar}
										photoUri={info.photo}
										placeholderUri={info.placeholder}
										displayName={info.fn}
									/>
									<Conversation.Operations visible>
										<ArrowButton
											title="Select Contact"
											direction="right"
											onClick={() => {
												selectContact(c);
												setSelectContact(null);
											}}
										/>
									</Conversation.Operations>
								</Conversation>
							);
						}}
					/>
				</Dialog>
			)}

			{popupMember && (
				<Dialog
					key={popupMember.id}
					open={!!popupMember}
					onClose={() => {
						setPopupMember(undefined);
						setAddRoleAnchorEl(null);
					}}
					id="profile-dialog"
				>
					<AvatarWithPlaceholder
						photoUri={popupMember.photoUri}
						placeholderUri={popupMember.placeholderUri}
						displayName={popupMember.displayName}
						size="fluid"
					/>

					{popupMember.chat && (
						<small>
							<code>{popupMember.chat.chatId}</code>
						</small>
					)}

					<div>
						{popupMember.roles.map((role) => (
							<span
								key={role.id}
								className="p-category"
								style={{ background: role.color() }}
							>
								{role.title}
								{activeChat?.canRemoveRole(popupMember, role) && (
									<Button
										icon={<FontAwesomeIcon icon={faXmark} />}
										title="Remove role"
										onClick={() => {
											activeChat?.removeRole(popupMember, role);
											setPopupMember(undefined);
											setAddRoleAnchorEl(null);
										}}
									/>
								)}
							</span>
						))}

						{(activeChat?.availableRoles(popupMember)?.length ?? 0) > 0 && (
							<>
								<Button
									icon={<FontAwesomeIcon icon={faPlus} />}
									title="Add role"
									onClick={(e) => {
										setAddRoleAnchorEl(e.currentTarget);
									}}
								/>
								<Menu
									open={!!addRoleAnchorEl}
									anchorEl={addRoleAnchorEl}
									onClose={() => setAddRoleAnchorEl(null)}
								>
									{activeChat?.availableRoles(popupMember).map((role) => (
										<MenuItem
											key={role.id}
											onClick={() => {
												activeChat?.addRole(popupMember, role);
												setPopupMember(undefined);
												setAddRoleAnchorEl(null);
											}}
										>
											{role.title}
										</MenuItem>
									))}
								</Menu>
							</>
						)}
					</div>

					<AwaitOnce
						resolve={() => popupMember.status(client)}
						fallback={<div />}
					>
						{(status) =>
							(status.emoji || status.text) && (
								<p className="status" title="Status">
									<span className="emoji">{status.emoji}</span>
									<span className="text">{status.text}</span>
								</p>
							)
						}
					</AwaitOnce>

					<AwaitOnce
						resolve={() => popupMember.profile(client)}
						fallback={<CircularProgress />}
					>
						{(profile) => <Profile profile={profile} />}
					</AwaitOnce>

					<Button
						onClick={() => {
							innerChatRef.current.addMention(popupMember);
							setPopupMember(undefined);
							setAddRoleAnchorEl(null);
						}}
					>
						Mention
					</Button>
				</Dialog>
			)}

			<Dialog
				open={scanQRCode}
				onClose={() => setScanQRCode(false)}
				fullScreen={isMobile}
				onClick={() => setScanQRCode(false)}
			>
				<video ref={qrScanVideo}></video>
				<p style={{ textAlign: "center", marginTop: "1em" }}>{qrStatus}</p>
			</Dialog>
		</MainContainer>
	);
};
